import { Eye, LoaderCircle, RotateCcw, Search } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import type { ReasonResultFilters } from '../store';
import type { ReasonResultSummary } from '../types';

interface ReasonResultsProps {
  results: ReasonResultSummary[];
  filters: ReasonResultFilters;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onSearch: (filters: ReasonResultFilters) => void;
  onLoadMore: () => void;
  onOpen: (episodeId: string) => void;
}

function toLocalInput(value: number | undefined): string {
  if (value === undefined) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(value - offset).toISOString().slice(0, 16);
}

function fromLocalInput(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDate(value: number): string {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);
}

function formatDuration(value: number): string {
  const totalSeconds = Math.max(0, Math.round(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}h ${String(minutes).padStart(2, '0')}m`
    : `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export function ReasonResults({
  results,
  filters,
  loading,
  loadingMore,
  hasMore,
  onSearch,
  onLoadMore,
  onOpen,
}: ReasonResultsProps) {
  const [episodeId, setEpisodeId] = useState(filters.episode_id ?? '');
  const [operator, setOperator] = useState(filters.operator ?? '');
  const [from, setFrom] = useState(toLocalInput(filters.from_ms));
  const [to, setTo] = useState(toLocalInput(filters.to_ms));
  const [limit, setLimit] = useState(filters.limit ?? 50);

  useEffect(() => {
    setEpisodeId(filters.episode_id ?? '');
    setOperator(filters.operator ?? '');
    setFrom(toLocalInput(filters.from_ms));
    setTo(toLocalInput(filters.to_ms));
    setLimit(filters.limit ?? 50);
  }, [filters]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const fromMs = fromLocalInput(from);
    const toMs = fromLocalInput(to);
    onSearch({
      ...(episodeId.trim() ? { episode_id: episodeId.trim() } : {}),
      ...(operator.trim() ? { operator: operator.trim() } : {}),
      ...(fromMs === undefined ? {} : { from_ms: fromMs }),
      ...(toMs === undefined ? {} : { to_ms: toMs }),
      limit,
    });
  };

  const reset = () => {
    const now = Date.now();
    const next = {
      from_ms: now - 7 * 24 * 60 * 60 * 1_000,
      to_ms: now,
      limit: 50,
    };
    setEpisodeId('');
    setOperator('');
    setFrom(toLocalInput(next.from_ms));
    setTo(toLocalInput(next.to_ms));
    setLimit(next.limit);
    onSearch(next);
  };

  return (
    <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-brand-text-primary">Results</h1>
          <p className="mt-1 text-sm text-brand-text-secondary">Submitted annotation results</p>
        </div>
        <span className="font-mono text-xs text-brand-text-secondary">{results.length} loaded</span>
      </div>

      <form
        onSubmit={submit}
        className="grid gap-3 border-y border-brand-border py-4 md:grid-cols-2 xl:grid-cols-[1.25fr_1fr_1fr_1fr_auto]"
      >
        <label className="text-xs text-brand-text-secondary">
          Episode ID
          <input
            value={episodeId}
            onChange={(event) => setEpisodeId(event.target.value)}
            placeholder="Episode ID or prefix"
            className="mt-1 h-10 w-full rounded-md border border-brand-border bg-brand-surface px-3 font-mono text-sm text-brand-text-primary outline-none focus:border-brand-orange"
          />
        </label>
        <label className="text-xs text-brand-text-secondary">
          Operator
          <input
            value={operator}
            onChange={(event) => setOperator(event.target.value)}
            placeholder="Exact operator"
            className="mt-1 h-10 w-full rounded-md border border-brand-border bg-brand-surface px-3 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
          />
        </label>
        <label className="text-xs text-brand-text-secondary">
          Completed from
          <input
            type="datetime-local"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-brand-border bg-brand-surface px-3 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
          />
        </label>
        <label className="text-xs text-brand-text-secondary">
          Completed to
          <input
            type="datetime-local"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-brand-border bg-brand-surface px-3 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
          />
        </label>
        <div className="flex items-end gap-2">
          <select
            aria-label="Rows per page"
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value))}
            className="h-10 rounded-md border border-brand-border bg-brand-surface px-2 text-sm text-brand-text-primary"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-orange px-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Apply
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={loading}
            aria-label="Reset filters"
            title="Reset filters"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary hover:text-brand-text-primary disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </form>

      <div className="mt-5 overflow-x-auto border border-brand-border">
        <table className="w-full min-w-[1180px] border-collapse text-left text-sm">
          <thead className="bg-brand-surface text-xs uppercase text-brand-text-secondary">
            <tr>
              <th className="px-4 py-3 font-medium">Episode ID</th>
              <th className="px-4 py-3 font-medium">Operator</th>
              <th className="px-4 py-3 font-medium">Completed</th>
              <th className="px-4 py-3 font-medium">Revision</th>
              <th className="px-4 py-3 font-medium">Segments</th>
              <th className="px-4 py-3 font-medium">Reasons</th>
              <th className="px-4 py-3 font-medium">Fail duration</th>
              <th className="px-4 py-3 font-medium">Taxonomy</th>
              <th className="px-4 py-3 font-medium">DDB Sync</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="w-16 px-4 py-3 font-medium">View</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {results.map((result) => (
              <tr key={result.episode_id} className="bg-brand-dark hover:bg-brand-surface/60">
                <td className="px-4 py-3 font-mono text-xs text-brand-text-primary">
                  {result.episode_id}
                </td>
                <td className="px-4 py-3 text-brand-text-primary">{result.operator}</td>
                <td className="px-4 py-3 text-brand-text-secondary">
                  {formatDate(result.completed_at_ms)}
                </td>
                <td className="px-4 py-3 font-mono">{result.revision}</td>
                <td className="px-4 py-3 font-mono">{result.segment_count}</td>
                <td className="px-4 py-3 font-mono">{result.reason_selection_count}</td>
                <td className="px-4 py-3 font-mono">{formatDuration(result.output_duration_s)}</td>
                <td className="px-4 py-3">V{result.submission_version || '?'}</td>
                <td className="px-4 py-3">{result.ddb_sync_status}</td>
                <td className="px-4 py-3">
                  <span className={result.source_changed ? 'text-amber-300' : 'text-emerald-400'}>
                    {result.source_changed ? 'Changed' : 'Current'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => onOpen(result.episode_id)}
                    aria-label={`View ${result.episode_id}`}
                    title="View result"
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary hover:border-brand-orange hover:text-brand-orange"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && results.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-brand-text-secondary">
            No submitted results found.
          </div>
        ) : null}
      </div>

      {hasMore ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-4 text-sm text-brand-text-primary disabled:opacity-50"
          >
            {loadingMore ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            Load more
          </button>
        </div>
      ) : null}
    </main>
  );
}
